import React from 'react';
import { CircularProgress } from '@mui/material';

// Lazy load all modals to avoid circular dependencies and improve performance
const RejectionModal = React.lazy(() =>
  import('@/shared/ui').then(m => ({ default: m.RejectionModal }))
);

const CreativeDetailModal = React.lazy(() =>
  import('@/features/dooh/creatives').then(m => ({
    default: m.CreativeDetailsModal,
  }))
);

const AddCreativeModal = React.lazy(() =>
  import('@/features/dooh/creatives').then(m => ({
    default: m.AddCreativeModal,
  }))
);

const SelectCreativeModal = React.lazy(() =>
  import('@/features/dooh/creatives').then(m => ({
    default: m.SelectCreativeModal,
  }))
);

const DeleteCreativeModal = React.lazy(() =>
  import('@/features/dooh/creatives').then(m => ({
    default: m.DeleteCreativeModal,
  }))
);

const DeleteCampaignModal = React.lazy(() =>
  import('@/features/dooh/campaigns').then(m => ({
    default: m.DeleteCampaignModal,
  }))
);

const ChangeCreativeTypeModal = React.lazy(() =>
  import('@/features/dooh/campaigns').then(m => ({
    default: m.ChangeCreativeTypeModal,
  }))
);

const ResetCreativesModal = React.lazy(() =>
  import('@/features/dooh/campaigns').then(m => ({
    default: m.ResetCreativesModal,
  }))
);

const ImagePreviewModal = React.lazy(() =>
  import('@/shared/ui').then(m => ({ default: m.ImagePreviewModal }))
);

const AdvertiserCustomCreativeModal = React.lazy(() =>
  import('@/features/dooh/campaigns').then(m => ({
    default: m.AdvertiserCustomCreativeModal,
  }))
);

const AddTargetGroupModal = React.lazy(() =>
  import('@/features/dooh/campaigns').then(m => ({
    default: m.AddTargetGroupModal,
  }))
);

const Snackbar = React.lazy(() =>
  import('@/shared/ui').then(m => ({ default: m.SnackbarComponent }))
);

const SlotAssignmentModal = React.lazy(() =>
  import('@/features/dooh/campaigns').then(m => ({
    default: m.SlotAssignmentModal,
  }))
);

const BrandCreativeModal = React.lazy(() =>
  import('@/features/dooh/creatives').then(m => ({
    default: m.BrandCreativeModal,
  }))
);

const MediaPreviewDialog = React.lazy(() =>
  import('@/features/dooh/screen').then(m => ({
    default: m.MediaPreviewDialog,
  }))
);

const SlotEditModal = React.lazy(() =>
  import('@/features/dooh/screen').then(m => ({
    default: m.SlotEditModal,
  }))
);

const BulkResultDialog = React.lazy(() =>
  import('@/features/dooh/screen').then(m => ({
    default: m.BulkResultDialog,
  }))
);

const AddEditMemberModal = React.lazy(() =>
  import('@/features/dooh/user-management').then(m => ({
    default: m.AddEditMemberModal,
  }))
);

const DeleteMemberModal = React.lazy(() =>
  import('@/features/dooh/user-management').then(m => ({
    default: m.DeleteMemberModal,
  }))
);

const CreateRoleModal = React.lazy(() =>
  import('@/features/dooh/user-management').then(m => ({
    default: m.CreateRoleModal,
  }))
);

const RoleDetailsModal = React.lazy(() =>
  import('@/features/dooh/user-management').then(m => ({
    default: m.RoleDetailsModal,
  }))
);

const CampaignScreenEditModal = React.lazy(() =>
  import('@/features/dooh/campaigns').then(m => ({
    default: m.CampaignScreenEditModal,
  }))
);

const ApprovalReviewModal = React.lazy(() =>
  import('@/features/dooh/approval').then(m => ({
    default: m.ApprovalReviewModal,
  }))
);

const CreativeApprovalDetailsModal = React.lazy(() =>
  import('@/features/dooh/approval').then(m => ({
    default: m.CreativeApprovalDetailsModal,
  }))
);

const CampaignApprovalDetailsModal = React.lazy(() =>
  import('@/features/dooh/approval').then(m => ({
    default: m.CampaignApprovalDetailsModal,
  }))
);

const ScreenApprovalDetailsModal = React.lazy(() =>
  import('@/features/dooh/approval').then(m => ({
    default: m.ScreenApprovalDetailsModal,
  }))
);

const LiveScreenPreview = React.lazy(() =>
  import('@/features/dooh/screen').then(m => ({
    default: m.LiveScreenPreview,
  }))
);

// Define component mapping interface
export interface ComponentConfig {
  id: string;
  component: string;
  props?: Record<string, any>;
}

// Component registry with lazy components
const componentRegistry: Record<
  string,
  React.LazyExoticComponent<React.ComponentType<any>>
> = {
  // Modal components
  RejectionModal,
  CreativeDetailModal,
  AddCreativeModal,
  SelectCreativeModal,
  DeleteCreativeModal,
  DeleteCampaignModal,
  ChangeCreativeTypeModal,
  ResetCreativesModal,
  ImagePreviewModal,
  AdvertiserCustomCreativeModal,
  AddTargetGroupModal,
  Snackbar,
  SlotAssignmentModal,
  BrandCreativeModal,
  MediaPreviewDialog,
  SlotEditModal,
  BulkResultDialog,
  AddEditMemberModal,
  DeleteMemberModal,
  CreateRoleModal,
  RoleDetailsModal,
  CampaignScreenEditModal,
  ApprovalReviewModal,
  CreativeApprovalDetailsModal,
  CampaignApprovalDetailsModal,
  ScreenApprovalDetailsModal,
  LiveScreenPreview,
};

/**
 * Renders a component by its ID with the provided props
 * @param id - Component identifier
 * @param props - Props to pass to the component
 * @returns React element or null if component not found
 */
export const renderComponentById = (
  id: string,
  props: Record<string, any> = {}
): React.ReactElement | null => {
  const Component = componentRegistry[id];

  if (!Component) {
    return null;
  }

  // Extract key from props if it exists, otherwise use id
  const { key, ...restProps } = props;
  const componentKey = key || id;

  return (
    <React.Suspense
      key={componentKey}
      fallback={
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            color: '#666',
          }}
        >
          <CircularProgress />
        </div>
      }
    >
      <Component key={componentKey} {...restProps} />
    </React.Suspense>
  );
};
